import { useState } from "react";
import { useLocation } from "react-router";

import DocumentationIconButton from "components/DocumentationIconButton";
import { WarningTriangleColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import DesktopView from "components/Mobile/DesktopView";
import PageWrapper from "components/PageWrapper";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";

import { getDefaultDateRange } from "./helpers";
import useAccountRunsSearch from "./useAccountRunsSearch";
import Runs from "./Wrapper";

const RunsWrapper = () => {
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const { search } = useLocation();
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`Runs · ${accountName}`);

  const { entities, error, loading, loaded, total, refetch } = useAccountRunsSearch(dateRange);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (error) {
    return (
      <EmptyState
        title="Couldn't load runs"
        caption="Something went wrong while loading this account's runs."
        icon={WarningTriangleColored}
        fullHeight
      >
        <Button variant="primary" onClick={refetch}>
          Try again
        </Button>
      </EmptyState>
    );
  }

  // initial load: full page; a reload shows progress in-content
  if (loading && entities.length === 0) {
    const message = total ? `Loading runs ${loaded} / ${total}…` : `Loading runs ${loaded}…`;

    return <PageLoading message={message} />;
  }

  return (
    <>
      <ViewHeader>
        <ViewHeaderTitle>Runs</ViewHeaderTitle>
        <DesktopView>
          <ViewHeaderWrapper direction="row">
            <DocumentationIconButton
              href={getDocsUrl("/concepts/run")}
              tooltipText="Go to runs documentation"
            />
          </ViewHeaderWrapper>
        </DesktopView>
      </ViewHeader>

      <PageWrapper>
        <Runs
          entities={entities}
          search={search}
          dateRange={dateRange}
          setDateRange={setDateRange}
          refetchData={refetch}
          loading={loading}
          loaded={loaded}
          total={total}
          isAccountWide
        />
      </PageWrapper>
    </>
  );
};

export default RunsWrapper;
